import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Formik } from 'formik'
import { isEmpty, keys } from 'lodash'

import {
  FilterHeader,
  FilterWrapper,
  TagsWrapper,
  FiltersWrapper,
  FilterToggle,
  FiltersContainer,
  FiltersSubmit
} from 'components/Filters'
import { Tag } from 'components/v2/Tag'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { SelectState } from 'features/Cities/components/SelectState'
import { STATES } from 'utils/states'
import { COUNTRY_CODES } from 'utils/countries'
import { GTBusinessClientSuggestion, GTBusinessSiteSuggestion } from 'gql/types.generated'
import { IOperationalCostsFilters } from 'features/OperationalCosts/types'
import { VehicleTemplateAutocomplete } from 'features/VehicleTemplates/components/VehicleTemplateAutocomplete'

const messages = defineMessages({
  businessClientPlaceholder: {
    id: 'operationalCosts.client.placeholder',
    defaultMessage: 'Enter a business client'
  },
  businessSitePlaceholder: {
    id: 'operationalCosts.site.placeholder',
    defaultMessage: 'Enter a business site'
  },
  statePlaceholder: {
    id: 'operationalCosts.state.placeholder',
    defaultMessage: 'Select a state'
  },
  vehicleTemplatePlaceholder: {
    id: 'operationalCosts.vehicleTemplate.placeholder',
    defaultMessage: 'Enter a vehicle template'
  }
})

const defaultValues: IOperationalCostsFilters = {
  businessClient: {
    id: '',
    name: ''
  },
  businessSite: {
    id: '',
    name: ''
  },
  state: {
    code: '',
    name: ''
  },
  vehicleTemplate: null
}

interface IOperationalCostsFiltersProps {
  filters: IOperationalCostsFilters
  onSubmit: (values: IOperationalCostsFilters) => void
}

export const OperationalCostsFilters = ({ filters, onSubmit }: IOperationalCostsFiltersProps): ReactElement => {
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const initialValues: IOperationalCostsFilters = {
    businessClient: filters.businessClient || defaultValues.businessClient,
    businessSite: filters.businessSite || defaultValues.businessSite,
    state: filters.state || defaultValues.state,
    vehicleTemplate: filters.vehicleTemplate || defaultValues.vehicleTemplate
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {(props): ReactElement => {
        const { setFieldValue, values } = props

        const handleFilterDelete = (id: keyof typeof defaultValues): void => {
          setFieldValue(id, defaultValues[id])
          onSubmit({
            ...values,
            [id]: defaultValues[id]
          })
        }

        const handleStateChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>): void => {
          const state = STATES.find((state) => state.code === value) || defaultValues.state
          setFieldValue('state', {
            code: value,
            name: state?.name
          })
          onSubmit({
            ...values,
            state
          })
        }

        return (
          <form>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey) => {
                      let label, value
                      const filterValue = filters[filterKey as keyof typeof filters]
                      const hasValue = !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'businessClient':
                          label = (
                            <FormattedMessage id="operationalCosts.client.label" defaultMessage="Business Client" />
                          )
                          value = (filterValue as { id: string; name: string }).name
                          break
                        case 'businessSite':
                          label = <FormattedMessage id="operationalCosts.site.label" defaultMessage="Business Site" />
                          value = (filterValue as { id: string; name: string }).name
                          break
                        case 'state':
                          label = <FormattedMessage id="operationalCosts.state.label" defaultMessage="State" />
                          value = (filterValue as { code: string; name: string }).name
                          break
                        case 'vehicleTemplate':
                          label = (
                            <FormattedMessage
                              id="operationalCosts.vehicleTemplate.label"
                              defaultMessage="Vehicle Template"
                            />
                          )
                          value = (filterValue as { id: string; name: string }).name
                          break
                      }

                      if (!value) return null

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof filters)}
                        >
                          {label}: {value as string}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <SelectState
                    id="stateCode"
                    value={values.state?.code || ''}
                    onChange={handleStateChange}
                    placeholder={intl.formatMessage(messages.statePlaceholder)}
                    country={COUNTRY_CODES.MEXICO}
                    label={<FormattedMessage id="operationalCosts.state.label" defaultMessage="State" />}
                  />
                  <BusinessClientAutocomplete
                    id="businessClientId"
                    value={values.businessClient?.name}
                    placeholder={intl.formatMessage(messages.businessClientPlaceholder)}
                    onChange={(businessClient: GTBusinessClientSuggestion): void => {
                      setFieldValue('businessClient', businessClient)
                      onSubmit({
                        ...values,
                        businessClient
                      })
                    }}
                    label={<FormattedMessage id="operationalCosts.client.label" defaultMessage="Business Client" />}
                  />
                  <BusinessSiteAutocomplete
                    id="businessSiteId"
                    value={values.businessSite?.name}
                    placeholder={intl.formatMessage(messages.businessSitePlaceholder)}
                    onChange={(businessSite: GTBusinessSiteSuggestion): void => {
                      setFieldValue('businessSite', businessSite)
                      onSubmit({
                        ...values,
                        businessSite
                      })
                    }}
                    label={<FormattedMessage id="operationalCosts.site.label" defaultMessage="Business Site" />}
                  />
                  <VehicleTemplateAutocomplete
                    id="vehicleTemplateId"
                    value={
                      values.vehicleTemplate
                        ? `${values.vehicleTemplate.name} (${values.vehicleTemplate.capacity})`
                        : ''
                    }
                    placeholder={intl.formatMessage(messages.vehicleTemplatePlaceholder)}
                    onChange={(vehicleTemplate): void => {
                      setFieldValue('vehicleTemplate', vehicleTemplate)
                      onSubmit({
                        ...values,
                        vehicleTemplate
                      })
                    }}
                    label={
                      <FormattedMessage id="operationalCosts.vehicleTemplate.label" defaultMessage="Vehicle Template" />
                    }
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
